import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { BusinessSectorDto } from '@jaam/commons/dtos';
import { PAYROLLR_API, AppErrorResponse } from '../services/axios';

interface BusinessSectorOption {
  value: number;
  label: string;
}

interface UseGetBusinessSectorsParams {
  onSuccess?: (data: BusinessSectorOption[]) => void;
  onError?: (error: AppErrorResponse) => void;
}

export const useGetBusinessSectors = ({
  onSuccess,
  onError,
}: UseGetBusinessSectorsParams = {}) => {
  const { t } = useTranslation();

  const {
    data: businessSectors,
    isLoading: isFetchingBusinessSectors,
    error: sectorsError,
    refetch,
  } = useQuery<BusinessSectorDto[], AppErrorResponse, BusinessSectorOption[]>({
    queryKey: ['business-sectors'],
    queryFn: async () => {
      const response = await PAYROLLR_API.get<BusinessSectorDto[]>('/business-sectors');
      return response.data;
    },
    select: (data) => {
      return (data ?? []).map((sector: BusinessSectorDto) => ({
        value: sector.id || 0,
        label: t(`businessSectors.${sector.code}`),
      }));
    },
    staleTime: 0,
  });

  if (onSuccess && businessSectors) onSuccess(businessSectors);
  if (onError && sectorsError) onError(sectorsError);

  return {
    businessSectors,
    isFetchingBusinessSectors,
    sectorsError,
    refetch,
  };
};
